import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, Row } from "@tanstack/react-table";
import cn from "classnames";
import { CSSProperties } from "react";

import { TableBodyProps } from "../ItemsTableDragDrop.types";

import styles from "../ItemsTableDragDrop.module.scss";

const TableBody = <TData extends { id: string; priority?: number }>({
  table,
  onRowClick,
  dataIds,
  isDragDisabled = false
}: TableBodyProps<TData>) => {
  const handleRowClick = (rowItem: TData) => {
    if (onRowClick) onRowClick(rowItem);
  };

  const DraggableRow = ({ row }: { row: Row<TData> }) => {
    const { transform, transition, setNodeRef, isDragging } = useSortable({
      id: row.original.id
    });

    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1 : undefined,
      position: "relative"
    };

    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={cn(styles.table_dataRow, {
          [styles.table_dataRow__clickable]: onRowClick
        })}
        onClick={() => handleRowClick(row.original)}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            key={cell.id}
            style={{ width: cell.column.getSize() }}
            className={styles.table_dataCell}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <tbody className={styles.table_body}>
      <SortableContext
        items={dataIds}
        strategy={verticalListSortingStrategy}
        disabled={isDragDisabled}
      >
        {table.getRowModel().rows.map((row) => (
          <DraggableRow key={row.id} row={row} />
        ))}
      </SortableContext>
    </tbody>
  );
};

export default TableBody;
